import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useLanguage } from "@/contexts/LanguageContext";

import yamilaImg from "@/assets/team/yamila.jpg";
import viviImg from "@/assets/team/vivi.jpg";
import inesImg from "@/assets/team/ines.jpg";
import argyImg from "@/assets/team/argy.jpg";
import cesarImg from "@/assets/team/cesar.jpg";
import martinImg from "@/assets/team/martin.jpg";

const team = [
  { name: "Yamila Villaverde", role: "Founder & CEO", image: yamilaImg },
  { name: "Martin Dos Santos", role: "Partnerships Lead", image: martinImg },
  { name: "César Galvis", role: "Head of Strategy & Analytics", image: cesarImg },
  { name: "Vivi Ramirez", role: "Data Analyst", image: viviImg },
  { name: "Argy", role: "Data & Communications Specialist", image: argyImg },
  { name: "Inés Marconato", role: "UX/UI & Visual Designer", image: inesImg },
];

const HomeTeamSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const { t } = useLanguage();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const contentY = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [120, 0, 0, -60]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.1, 0.9, 1], [0, 1, 1, 0.3]);

  return (
    <section id="equipo" className="py-24 md:py-32 overflow-hidden bg-background" ref={ref}>
      <motion.div className="max-w-7xl mx-auto px-6 mb-12" style={{ y: contentY, opacity: contentOpacity }}>
        <motion.div>
          <motion.p
            className="text-xs uppercase tracking-[0.25em] font-bold text-brand-uva mb-4 text-popover-foreground"
            initial={{ opacity: 0, x: -30, filter: "blur(6px)" }}
            animate={inView ? { opacity: 1, x: 0, filter: "blur(0px)" } : {}}
            transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            {t("team.tag")}
          </motion.p>
          <h2 className="text-3xl md:text-5xl font-black tracking-tight lowercase mb-4">
            <span className="overflow-hidden inline-block">
              <motion.span
                className="inline-block"
                initial={{ y: "100%" }}
                animate={inView ? { y: "0%" } : {}}
                transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              >
                {t("team.title1")}
              </motion.span>
            </span>{" "}
            <span className="overflow-hidden inline-block">
              <motion.span
                className="inline-block text-brand-uva"
                initial={{ y: "100%" }}
                animate={inView ? { y: "0%" } : {}}
                transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              >
                {t("team.title2")}
              </motion.span>
            </span>
          </h2>
          <motion.p
            className="text-lg text-muted-foreground max-w-2xl"
            initial={{ opacity: 0, filter: "blur(8px)" }}
            animate={inView ? { opacity: 1, filter: "blur(0px)" } : {}}
            transition={{ duration: 0.7, delay: 0.35 }}
          >
            {t("team.desc")}
          </motion.p>
        </motion.div>
      </motion.div>

      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-0 rounded-2xl overflow-hidden shadow-lg ring-1 ring-black/10"
          initial={{ opacity: 0, filter: "blur(10px)" }}
          animate={inView ? { opacity: 1, filter: "blur(0px)" } : {}}
          transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          {team.map((member, i) => (
            <motion.div
              key={member.name}
              className="relative aspect-[3/4] overflow-hidden group cursor-pointer"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.5 + i * 0.08 }}
            >
              <img
                src={member.image}
                alt={member.name}
                width={260}
                height={347}
                className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 ${member.name === "Martin Dos Santos" ? "scale-125 object-top group-hover:scale-[1.35]" : ""}`}
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/50 group-hover:bg-black/0 transition-colors duration-500" />
              <div className="absolute inset-x-0 bottom-0 p-3">
                <h3 className="text-xs font-black uppercase tracking-wide text-white group-hover:text-brand-uva transition-colors duration-300 mb-0.5">{member.name}</h3>
                <p className="text-[10px] text-white/80">{member.role}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default HomeTeamSection;
